import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { callAPI } from "../../helper/apiUtils";
import { $crud } from "../../helper/CrudFactory";
import LanguageContext from "../../helper/LanguageContext";
import { BASE_URL, getAvatarInitials } from "../../helper/constants";
import CustomScrollbars from "../../helper/CustomScrollbars";
import RoomSectionHeader from "./RoomSectionHeader";
// The room-list styles (section headers, skeleton, empty line) live there.
import "./MyProject.css";
import "./OversightRoomList.css";

// Rooms per company, kept across visits so the list shows at once and then
// refreshes in the background.
const roomCache = new Map();

// /admin/company/:id/building row -> list row (the mobile Admin Center's mapping).
function toRoom(b) {
  const flag = b.buildingProjectType ?? b.buildingCheck;
  return {
    id: String(b.id),
    name: String(b.name || b.street || "").trim(),
    street: b.street || "",
    city: b.city || "",
    image: b.image || null,
    themeColor: b.themeColor || null,
    isInternal: flag === true || flag === 1 || flag === "1",
    kindKnown: flag != null,
    status: b.isArchived ? "archived" : b.isDone ? "completed" : "active",
  };
}

async function fetchCompanyRooms(companyId) {
  const { data } = await callAPI(`/admin/company/${companyId}/building`, { search: "" }, "GET");
  if (data && data.status) return (Array.isArray(data.data) ? data.data : []).map(toRoom);
  // A company without rooms answers 400 "There is no building list found".
  if (/no building list/i.test((data && data.message) || "")) return [];
  throw new Error((data && data.message) || "");
}

function labels(strings, isGerm) {
  const pick = (key, de, en) => strings[key] || (isGerm ? de : en);
  return {
    all: pick("filter_all", "Alle", "All"),
    internal: pick("internal_project", "Intern", "Internal"),
    external: pick("external_projects", "Extern", "External"),
    description: pick(
      "oversight_rooms_description",
      "Wähle ein Projekt, um die zugehörigen Mitglieder sowie alle gesammelten Medien und Chats aller Mitarbeitenden anzuzeigen.",
      "Select a project to view the associated members as well as all collected media and chats of all employees."
    ),
    archivedRooms: pick("archived_rooms", "Archivierte Projekte", "Archived projects"),
    completedRooms: pick("completed_rooms", "Abgeschlossene Projekte", "Completed projects"),
    noRooms: pick("oversight_no_rooms", "Keine Projekte gefunden", "No projects found"),
    noMatch: pick("oversight_no_rooms_match", "Keine Projekte gefunden, die der Suche entsprechen", "No projects match your search"),
    loadError: pick("oversight_rooms_load_error", "Die Projekte konnten nicht geladen werden.", "The projects could not be loaded."),
    retry: pick("cockpit_retry", "Erneut versuchen", "Try again"),
    archive: pick("oversight_archive_room", "Projekt archivieren", "Archive project"),
    restore: pick("oversight_restore_room", "Projekt wiederherstellen", "Restore project"),
    archiveTitle: pick("oversight_archive_room_title", "Dieses Projekt archivieren?", "Archive this project?"),
    restoreTitle: pick("oversight_restore_room_title", "Dieses Projekt wiederherstellen?", "Restore this project?"),
    archiveDesc: pick(
      "oversight_archive_room_desc",
      "Das Projekt \"{room}\" wird archiviert und in der App ausgeblendet. Diese Funktion ist nur für interne Projekte verfügbar.",
      "The project \"{room}\" will be archived and hidden from the app. This function is only available for internal projects."
    ),
    restoreDesc: pick(
      "oversight_restore_room_desc",
      "Das Projekt \"{room}\" wird wiederhergestellt und ist in der App wieder sichtbar. Diese Funktion ist nur für interne Projekte verfügbar.",
      "The project \"{room}\" will be restored and visible in the app again. This function is only available for internal projects."
    ),
    archiveAction: pick("oversight_archive_action", "Archivieren", "Archive"),
    restoreAction: pick("oversight_restore_action", "Wiederherstellen", "Restore"),
    cancel: pick("cancel", "Abbrechen", "Cancel"),
    archiveSuccess: pick("oversight_archive_room_success", "Projekt erfolgreich archiviert", "Project archived successfully"),
    restoreSuccess: pick("oversight_restore_room_success", "Projekt erfolgreich wiederhergestellt", "Project restored successfully"),
    archiveError: pick("oversight_archive_room_error", "Archivierung des Projekts fehlgeschlagen", "Failed to archive project"),
    restoreError: pick("oversight_restore_room_error", "Wiederherstellung des Projekts fehlgeschlagen", "Failed to restore project"),
    buildingFallback: isGerm ? "Gebäude" : "Building",
  };
}

const RoomsSkeleton = () => (
  <div className="scroll latestmessages_scrollbar loading my-project__skeleton">
    {[1, 2, 3, 4, 5, 6].map((item, index) => (
      <div
        key={item}
        className="company_services m-0 chat_services user_info_chat left_users p-1 my-project__skeleton-item"
        style={{ "--skeleton-delay": `${index * 0.05}s` }}
      >
        <div className="company_centact my-project__skeleton-row">
          <div className="skeleton skeleton-image my-project__skeleton-image"></div>
          <div className="my-project__skeleton-lines">
            <div className="skeleton skeleton-text my-project__skeleton-line"></div>
            <div className="skeleton skeleton-text short my-project__skeleton-line--short"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

/**
 * Räume > Oversight: every room of a company the viewer manages, laid out like
 * the Extern/Intern lists. A row opens the room's oversight view (onOpenRoom);
 * internal rooms can be archived and restored. The parent keys this by company,
 * so a new pick starts with a fresh filter and load.
 *
 *   companyId       the managed company
 *   query           the header search (name, street or city)
 *   activeRoomId    the room open in the room column
 *   onOpenRoom(room)
 *   onRoomsChanged()  after an archive toggle (the viewer's own lists may change)
 *   className       extra class on the list container (another host's layout)
 */
export default function OversightRoomList({ companyId, query = "", activeRoomId, onOpenRoom, onRoomsChanged, className = "" }) {
  const { strings, isGerm } = useContext(LanguageContext);
  const t = labels(strings || {}, isGerm);
  const cacheKey = String(companyId);
  const [rooms, setRooms] = useState(() => roomCache.get(cacheKey) || null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState({ archived: false, completed: false });
  // Rooms whose archive toggle is in flight (the endpoint toggles, so never twice).
  const [busy, setBusy] = useState({});
  const inFlight = useRef(new Set());
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const list = await fetchCompanyRooms(companyId);
      roomCache.set(cacheKey, list);
      if (alive.current) {
        setRooms(list);
        setError(false);
      }
    } catch (e) {
      if (alive.current && !roomCache.has(cacheKey)) setError(true);
    }
  }, [companyId, cacheKey]);

  useEffect(() => {
    load();
  }, [load]);

  const roomName = (r) => r.name || `${t.buildingFallback} #${r.id}`;
  const term = query.trim().toLowerCase();

  // A search opens the sections so no match stays hidden.
  useEffect(() => {
    if (term) setExpanded({ archived: true, completed: true });
  }, [term]);

  const rows = useMemo(
    () =>
      (rooms || []).filter(
        (r) =>
          (filter === "all" || (filter === "internal") === r.isInternal) &&
          (!term ||
            r.name.toLowerCase().includes(term) ||
            r.street.toLowerCase().includes(term) ||
            r.city.toLowerCase().includes(term))
      ),
    [rooms, filter, term]
  );

  // The filter only means something when the company has both kinds of rooms.
  const showFilter = useMemo(
    () => (rooms || []).some((r) => r.isInternal) && (rooms || []).some((r) => !r.isInternal),
    [rooms]
  );

  const toggleArchive = async (r) => {
    if (inFlight.current.has(r.id)) return;
    const archived = r.status === "archived";
    try {
      document.body.setAttribute("data-confirm-color", "green");
      await $crud.confirm({
        title: archived ? t.restoreTitle : t.archiveTitle,
        textContent: (archived ? t.restoreDesc : t.archiveDesc).replace("{room}", roomName(r)),
        options: { ok: archived ? t.restoreAction : t.archiveAction, cancel: t.cancel },
      });
    } catch (e) {
      return; // cancelled
    } finally {
      document.body.removeAttribute("data-confirm-color");
    }

    inFlight.current.add(r.id);
    setBusy((prev) => ({ ...prev, [r.id]: true }));
    try {
      const { data } = await callAPI(`/admin/building/${r.id}/hide`, {}, "PUT");
      if (!data || data.status === false) throw new Error((data && data.message) || "");
      const next = (rooms || []).map((x) => (x.id === r.id ? { ...x, status: archived ? "active" : "archived" } : x));
      roomCache.set(cacheKey, next);
      if (alive.current) setRooms(next);
      $crud.notify({ type: "success", message: archived ? t.restoreSuccess : t.archiveSuccess });
      // Like the Admin Center: reload after the toggle (a restored room may be completed).
      load();
      if (onRoomsChanged) onRoomsChanged();
    } catch (e) {
      $crud.notify({ type: "error", message: (e && e.message) || (archived ? t.restoreError : t.archiveError) });
    } finally {
      inFlight.current.delete(r.id);
      if (alive.current) setBusy((prev) => ({ ...prev, [r.id]: false }));
    }
  };

  const renderRow = (r) => {
    const archived = r.status === "archived";
    const inactive = r.status !== "active";
    const isOpen = activeRoomId != null && String(activeRoomId) === r.id;
    const name = roomName(r);
    const kind = r.kindKnown ? (r.isInternal ? t.internal : t.external) : "";
    const meta = [kind, r.city].filter(Boolean).join(" · ");
    const open = () => onOpenRoom && onOpenRoom(r);
    return (
      <div
        key={r.id}
        className={`project_info pr-3 oversight-list__row${isOpen ? " active-project" : ""}${inactive ? " oversight-list__row--inactive" : ""}`}
      >
        <div
          role="button"
          tabIndex={0}
          className="Project_name kj-flex-1 oversight-list__main"
          onClick={open}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              open();
            }
          }}
          title={name}
        >
          <div className="position-relative oversight-list__avatar">
            {r.image ? (
              <img src={BASE_URL + r.image} alt="" className="project_icon oversight-list__icon-img" />
            ) : (
              <div
                className={`project_icon oversight-list__icon${r.isInternal ? " oversight-list__icon--internal" : ""}`}
                style={r.themeColor ? { "--project-theme-color": r.themeColor } : undefined}
              >
                {getAvatarInitials(name)}
              </div>
            )}
            <span
              className={`material-symbols-outlined admin-view-eye oversight-list__eye${r.isInternal ? " oversight-list__eye--internal" : ""}`}
              aria-hidden="true"
            >
              visibility
            </span>
          </div>
          <div className="p_name oversight-list__text">
            <span className="oversight-list__name">{name}</span>
            {meta ? <span className="oversight-list__meta">{meta}</span> : null}
          </div>
        </div>
        {r.isInternal ? (
          <div className="project_action oversight-list__action">
            <button
              type="button"
              className={`kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--internal oversight-list__archive${archived ? " is-archived" : ""}`}
              title={archived ? t.restore : t.archive}
              aria-label={archived ? t.restore : t.archive}
              disabled={!!busy[r.id]}
              onClick={() => toggleArchive(r)}
            >
              <span className="material-symbols-outlined">{archived ? "unarchive" : "archive"}</span>
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  if (!rooms) {
    return error ? (
      <div className="oversight-list__state">
        <p>{t.loadError}</p>
        <button type="button" className="kj-btn kj-btn--sm kj-btn--secondary" onClick={() => { setError(false); load(); }}>
          {t.retry}
        </button>
      </div>
    ) : (
      <RoomsSkeleton />
    );
  }

  const activeRows = rows.filter((r) => r.status === "active");
  const sections = [
    { key: "archived", title: t.archivedRooms, items: rows.filter((r) => r.status === "archived") },
    { key: "completed", title: t.completedRooms, items: rows.filter((r) => r.status === "completed") },
  ];

  return (
    <div className={`my_project px-2 pt-0 p-0 m-0 border-radius-0 project_list_div company-content-fade-in my-project__list my-project__list--member my-project__list--border-0 oversight-list${className ? ` ${className}` : ""}`}>
      <div className="oversight-list__intro">
        {showFilter ? (
          <div className="oversight-list__filters" role="tablist">
            {[
              ["all", t.all],
              ["internal", t.internal],
              ["external", t.external],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={filter === key}
                className={`kj-tab oversight-list__filter${filter === key ? " is-active" : ""}`}
                onClick={() => setFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}
        <p className="oversight-list__description">{t.description}</p>
      </div>
      <CustomScrollbars autoHide autoHideTimeout={200} autoHideDuration={200} className="bottom-bar">
        <div className="my-project__room-list">
          {activeRows.map(renderRow)}
          {sections.map((section) => {
            if (!section.items.length) return null;
            const open = !!expanded[section.key];
            return (
              <React.Fragment key={section.key}>
                <RoomSectionHeader
                  title={section.title}
                  expanded={open}
                  onClick={() => setExpanded((prev) => ({ ...prev, [section.key]: !open }))}
                />
                {open ? section.items.map(renderRow) : null}
              </React.Fragment>
            );
          })}
          {!rows.length ? (
            <div className="my-project__empty">{rooms.length ? t.noMatch : t.noRooms}</div>
          ) : null}
        </div>
      </CustomScrollbars>
    </div>
  );
}
